export interface ActionCreator {
  type: string;
  payload?: unknown;
}

type SnackbarVariant = 'error' | 'success' | 'warning' | 'info';
export interface Snackbar {
  title?: string | React.ReactNode;
  content?: React.ReactNode;
  message?: string;
  dataTestId?: string;
  className?: string;
  dismissible?: boolean;
  autoHideDuration?: number;
  variant?: SnackbarVariant;
  key?: string;
}

export interface SnackbarState {
  snackbars: Snackbar[];
}

// Initial state
export const initialState: SnackbarState = {
  snackbars: [],
};

// Actions
export const ADD_SNACKBAR = 'ADD_SNACKBAR';
export const REMOVE_SNACKBAR = 'REMOVE_SNACKBAR';

// Action creators
export const enqueueSnackbar = (item: Snackbar | string) => {
  return { type: ADD_SNACKBAR, payload: item };
};

export const closeSnackbar = (key?: string) => {
  return { type: REMOVE_SNACKBAR, payload: key };
};

const variantPriority: { [K in SnackbarVariant]: number } = {
  success: 1,
  error: 2,
  warning: 3,
  info: 4,
};

const sortTemporarySnackbars = (a: Snackbar, b: Snackbar) => {
  const priorityA = variantPriority[a.variant ?? 'info'];
  const priorityB = variantPriority[b.variant ?? 'info'];
  return priorityA - priorityB;
};

const createSnackbars = (currentSnackbars: Snackbar[], snackbar: Snackbar) => {
  if (snackbar.dismissible === false) {
    currentSnackbars.unshift(snackbar);
  } else {
    currentSnackbars.push(snackbar);
  }

  return currentSnackbars;
};

const displaySnackbars = (
  snackbar: Snackbar,
  currentSnackbarsOg: Snackbar[]
) => {
  const currentSnackbars = [...currentSnackbarsOg];
  const temporarySnackbars = currentSnackbars.filter(
    (snack) => snack.dismissible || snack.autoHideDuration
  );
  const staticSnackbars = currentSnackbars.filter(
    (snack) => !snack.dismissible && !snack.autoHideDuration
  );

  if (currentSnackbars.find((snack) => snack.key === snackbar.key)) {
    console.warn('Prevented from using duplicates');
    return currentSnackbars;
  }

  // adding a temporary snackbar, so the limit of 3 doesn't apply
  if (snackbar.dismissible || snackbar.autoHideDuration) {
    createSnackbars(temporarySnackbars, snackbar);
  } else {
    if (staticSnackbars.length >= 3) {
      staticSnackbars.shift();
      createSnackbars(staticSnackbars, snackbar);
    } else {
      createSnackbars(staticSnackbars, snackbar);
    }
  }

  temporarySnackbars.sort(sortTemporarySnackbars);
  return [...temporarySnackbars, ...staticSnackbars];
};

const removeSnackbars = (currentSnackbarsOg: Snackbar[], key: string) => {
  const currentSnackbars = [...currentSnackbarsOg];

  if (!key) {
    return [];
  }

  const foundIndex = currentSnackbars.findIndex((snack) => key === snack.key);

  if (foundIndex >= 0) {
    currentSnackbars.splice(foundIndex, 1);

    return currentSnackbars;
  }

  return currentSnackbars;
};

// Reducer
export const reducer = (state: SnackbarState, action: ActionCreator) => {
  switch (action.type) {
    case ADD_SNACKBAR: {
      const snackbar =
        typeof action.payload === 'string'
          ? { message: action.payload }
          : (action.payload as Snackbar);
      return {
        ...state,
        snackbars: displaySnackbars(snackbar, state.snackbars),
      };
    }
    case REMOVE_SNACKBAR: {
      const key = action.payload as string | undefined;
      return {
        ...state,
        snackbars: removeSnackbars(state.snackbars, key ?? ''),
      };
    }
    default:
      return state;
  }
};
